/**
 * Structured Definition Builder Component
 *
 * Build structured criterion definitions with target, constraints, and codes.
 */

import { useState } from 'react';
import { Plus, X, Code } from 'lucide-react';
import type {
  StructuredDefinition,
  TargetType,
  Constraint,
  ConstraintOperator,
  CodeReference,
  TARGET_TYPE_LABELS,
  OPERATOR_LABELS,
  CODE_SYSTEMS,
} from '../../types';

interface StructuredDefinitionBuilderProps {
  value: StructuredDefinition;
  onChange: (definition: StructuredDefinition) => void;
  error?: string;
}

const TARGET_TYPES: Array<{ value: TargetType; label: string }> = [
  { value: 'condition', label: 'Condition/Disease' },
  { value: 'medication', label: 'Medication' },
  { value: 'lab_value', label: 'Laboratory Value' },
  { value: 'demographic', label: 'Demographic' },
  { value: 'procedure', label: 'Procedure/Surgery' },
  { value: 'lifestyle', label: 'Lifestyle Factor' },
  { value: 'reproductive', label: 'Reproductive Status' },
  { value: 'consent', label: 'Consent' },
];

const OPERATORS: Array<{ value: ConstraintOperator; label: string }> = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater than or equal' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less than or equal' },
  { value: 'in', label: 'is one of' },
  { value: 'within', label: 'within timeframe' },
  { value: 'current', label: 'currently active' },
  { value: 'stable_dose', label: 'stable dose' },
  { value: 'duration_gte', label: 'duration at least' },
];

const CODE_SYSTEM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'ICD-10', label: 'ICD-10 (Diagnoses)' },
  { value: 'ICD-9', label: 'ICD-9 (Legacy)' },
  { value: 'SNOMED', label: 'SNOMED CT' },
  { value: 'NDC', label: 'NDC (Drugs)' },
  { value: 'LOINC', label: 'LOINC (Labs)' },
  { value: 'CPT', label: 'CPT (Procedures)' },
  { value: 'RxNorm', label: 'RxNorm (Meds)' },
];

export function StructuredDefinitionBuilder({
  value,
  onChange,
  error,
}: StructuredDefinitionBuilderProps) {
  const [showCodes, setShowCodes] = useState((value.codes?.length || 0) > 0);

  const updateField = <K extends keyof StructuredDefinition>(
    field: K,
    fieldValue: StructuredDefinition[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const addConstraint = () => {
    const newConstraint: Constraint = { operator: 'eq', value: '', unit: '' };
    updateField('constraints', [...(value.constraints || []), newConstraint]);
  };

  const updateConstraint = (index: number, constraint: Constraint) => {
    const newConstraints = [...(value.constraints || [])];
    newConstraints[index] = constraint;
    updateField('constraints', newConstraints);
  };

  const removeConstraint = (index: number) => {
    const newConstraints = [...(value.constraints || [])];
    newConstraints.splice(index, 1);
    updateField('constraints', newConstraints);
  };

  const addCode = () => {
    const newCode: CodeReference = { system: 'ICD-10', code: '' };
    updateField('codes', [...(value.codes || []), newCode]);
    setShowCodes(true);
  };

  const updateCode = (index: number, code: CodeReference) => {
    const newCodes = [...(value.codes || [])];
    newCodes[index] = code;
    updateField('codes', newCodes);
  };

  const removeCode = (index: number) => {
    const newCodes = [...(value.codes || [])];
    newCodes.splice(index, 1);
    updateField('codes', newCodes);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 space-y-4">
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Target type and value */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Type</label>
          <select
            value={value.target_type}
            onChange={e => updateField('target_type', e.target.value as TargetType)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {TARGET_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Value</label>
          <input
            type="text"
            value={value.target_value}
            onChange={e => updateField('target_value', e.target.value)}
            placeholder="e.g., Type 2 Diabetes, eGFR, age"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Drug class (for medications) */}
      {value.target_type === 'medication' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Drug Class</label>
          <input
            type="text"
            value={value.drug_class || ''}
            onChange={e => updateField('drug_class', e.target.value)}
            placeholder="e.g., anticoagulants, NSAIDs, beta-blockers"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      )}

      {/* Negation */}
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={value.negation}
          onChange={e => updateField('negation', e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 focus:ring-red-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Negation (looking for <span className="font-medium text-red-600 dark:text-red-400">absence</span> of this
          condition)
        </span>
      </label>

      {/* Constraints */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Constraints</label>
          <button
            type="button"
            onClick={addConstraint}
            className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <Plus size={14} />
            Add Constraint
          </button>
        </div>

        {(value.constraints || []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No constraints defined</p>
        ) : (
          <div className="space-y-2">
            {(value.constraints || []).map((constraint, i) => (
              <div key={i} className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                <select
                  value={constraint.operator}
                  onChange={e =>
                    updateConstraint(i, { ...constraint, operator: e.target.value as ConstraintOperator })
                  }
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={String(constraint.value || '')}
                  onChange={e => updateConstraint(i, { ...constraint, value: e.target.value })}
                  placeholder="Value"
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />

                <input
                  type="text"
                  value={constraint.unit || ''}
                  onChange={e => updateConstraint(i, { ...constraint, unit: e.target.value })}
                  placeholder="Unit"
                  className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />

                <button
                  type="button"
                  onClick={() => removeConstraint(i)}
                  className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Medical codes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            <Code size={14} className="inline mr-1" />
            Medical Codes (optional)
          </label>
          <button
            type="button"
            onClick={addCode}
            className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <Plus size={14} />
            Add Code
          </button>
        </div>

        {(value.codes || []).length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No codes defined</p>
        ) : (
          <div className="space-y-2">
            {(value.codes || []).map((code, i) => (
              <div key={i} className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                <select
                  value={code.system}
                  onChange={e =>
                    updateCode(i, { ...code, system: e.target.value as CodeReference['system'] })
                  }
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {CODE_SYSTEM_OPTIONS.map(sys => (
                    <option key={sys.value} value={sys.value}>
                      {sys.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  value={code.code}
                  onChange={e => updateCode(i, { ...code, code: e.target.value })}
                  placeholder="Code"
                  className="w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />

                <input
                  type="text"
                  value={code.description || ''}
                  onChange={e => updateCode(i, { ...code, description: e.target.value })}
                  placeholder="Description"
                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />

                <button
                  type="button"
                  onClick={() => removeCode(i)}
                  className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
